import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Card, CardContent, PopoverMenu } from '@guildeducationinc/recess';
import ArrowDropDownIcon from '@guildeducationinc/recess/icons/ArrowDropDownIcon';
import { CommentModal } from '@src/components/Manager/CommentModal/CommentModal';
import { useAuth } from '@src/hooks/useAuth';
import { WorkflowRunTaskStatus, WorkflowType } from '@src/types';
import { GlobalPermission } from '@src/utilities/permissions';
import { PlusIcon } from 'lucide-react';
import { TrackableButton, trackClickEvent, useBreakpoints, useLocale } from 'pe-core-frontend';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ApproveCard.module.scss';
export const ApproveCard = ({ approval }) => {
    const { hasPermission } = useAuth();
    const { t } = useLocale();
    const { isMobile } = useBreakpoints();
    const navigate = useNavigate();
    const [approvalAction, setApprovalAction] = useState();
    const handleActionClick = (action, localizedText = '') => {
        setApprovalAction(action);
        if (isMobile) {
            trackClickEvent(action, 'mobile_action', 'button', localizedText);
        }
    };
    if (!hasPermission(GlobalPermission.APPROVE_PAYMENT_REQUEST, GlobalPermission.APPROVE_PROGRAM_REQUEST)) {
        return null;
    }
    if (![WorkflowRunTaskStatus.PENDING, WorkflowRunTaskStatus.IN_PROGRESS].includes(approval.status)) {
        return null;
    }
    return (_jsx(Card, { className: styles.container, children: _jsxs(CardContent, { className: styles.approveCard, children: [_jsx("div", { className: styles.actions, children: (isMobile && (_jsx(PopoverMenu, { id: 'approve-actions', actions: [
                            {
                                name: t('Add comment'),
                                type: 'action',
                                onClick: () => handleActionClick('comment', t('Add comment')),
                            },
                            {
                                name: t('Request info'),
                                type: 'action',
                                onClick: () => handleActionClick('info', t('Request info')),
                            },
                            {
                                name: t('Reject'),
                                type: 'action',
                                onClick: () => handleActionClick('reject', t('Reject')),
                                intent: 'danger',
                            },
                            ...(approval.workflowType === WorkflowType.BUDGET_APPROVAL
                                ? [
                                    {
                                        name: t('Adjust amount'),
                                        className: styles.popoverAdjustAmountBtn,
                                        type: 'action',
                                        intent: 'primary',
                                        onClick: () => handleActionClick('adjust_amount', t('Adjust amount')),
                                    },
                                ]
                                : []),
                            {
                                name: t('Approve'),
                                className: styles.popoverApproveBtn,
                                type: 'action',
                                intent: 'primary',
                                onClick: () => handleActionClick('approve', t('Approve')),
                            },
                        ], children: _jsx(TrackableButton, { enText: 'Take action', tag: 'mobile_actions', appearance: 'ghost', fullWidth: true, children: _jsxs("span", { className: 'flex items-center gap-1', children: [t('Take action'), " ", _jsx(ArrowDropDownIcon, { size: 20 })] }) }) }))) || (_jsxs(_Fragment, { children: [_jsx(TrackableButton, { enText: 'Add comment', tag: 'desktop_action', appearance: 'ghost', onClick: () => setApprovalAction('comment'), children: _jsxs("span", { className: 'flex items-center gap-1', children: [t('Add comment'), " ", _jsx(PlusIcon, { size: 20 })] }) }), _jsx(TrackableButton, { enText: 'Request info', tag: 'desktop_action', appearance: 'outline', onClick: () => setApprovalAction('info'), children: t('Request info') }), _jsx(TrackableButton, { enText: 'Reject', tag: 'desktop_action', appearance: 'primary', className: styles.rejectBtn, onClick: () => setApprovalAction('reject'), children: t('Reject') }), approval.workflowType === WorkflowType.BUDGET_APPROVAL && (_jsx(TrackableButton, { enText: 'Adjust amount', tag: 'desktop_action', appearance: 'primary', className: styles.adjustAmountBtn, onClick: () => setApprovalAction('adjust_amount'), children: t('Adjust amount') })), _jsx(TrackableButton, { enText: 'Approve', tag: 'desktop_action', appearance: 'primary', className: styles.approveBtn, onClick: () => setApprovalAction('approve'), children: t('Approve') })] })) }), approvalAction && (_jsx(CommentModal, { approval: approval, action: approvalAction, onClose: () => {
                        setApprovalAction(undefined);
                    }, onComplete: () => {
                        if (approvalAction !== 'comment') {
                            navigate('/tasks');
                        }
                    } }))] }) }));
};
